// MediaLibraryPicker — the "reuse in context" modal (deck states 03 grid / 06
// list; design-handoff/concepts/media-library/03-builder-picker + 06-builder-picker-list).
// Shared, presentational component: opened from ANY surface that attaches media
// to a Segment/Wheel (DL-048, Gordon universal-access addendum 2026-07-14) so an
// already-uploaded asset is REUSED (nothing uploaded twice). Composes the same
// tabs/toolbar/grid/list as the Media Library page, so the two surfaces can never
// diverge. Data is fed in by MediaLibraryPickerConnected (no fetch here).
//
// Context type-filtering (addendum): `acceptedTypes` (from the consumer's existing
// type restriction) hides entirely-ineligible tabs, renders individually-ineligible
// items disabled with a plain-language reason, and blocks Insert for an ineligible
// selection (enforced in logic via canInsert, not just the disabled attribute).
//
// Picker-filter spec (Gordon 2026-07-24): the DEFAULT view goes further than
// disabling — items outside acceptedTypes are HIDDEN entirely (not just
// disabled), on every tab (all/captures included — the type filter composes
// with whichever tab is active, since 'captures' is a SOURCE filter and 'all'
// has no type filter of its own). A "Show all" toggle (default OFF, only
// rendered when the context actually restricts a type) flips that off: ON
// reveals the hidden items again, still disabled/grayed/non-selectable exactly
// as before. Toggle lives here (not in a per-consumer surface) so every
// consumer of this shared picker gets it automatically.
//
// EW-010 honesty: reuse is refcount-incrementing on the BE (WL-072); the caption
// chip states the contract ("Reused across Wheels — nothing is uploaded twice").
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as S from "./MediaLibrary.styled";
import { MediaGrid, MediaListTable } from "./MediaGrid";
import ErrorWheel from "../ErrorWheel";
import {
  IconClose, IconSearch, IconAllTiles, IconImage, IconVideo, IconAudio,
  IconDocument, IconGridLarge, IconList, IconTarget, IconCapture,
} from "./icons";
import { TYPE_TABS, filterMedia, sortMedia } from "../../pages/MediaLibrary/helpers";
import {
  pickerTypeTabs, defaultActiveTab, isItemEligible, ineligibleReason, canInsert,
  hasTypeRestriction, hiddenByTypeCount, tabOnShowAllOn, tabOnShowAllOff,
} from "../SegmentBuilder/Shared/MediaSelection/librarySourceLogic";

const TAB_ICON = { all: IconAllTiles, images: IconImage, videos: IconVideo, audio: IconAudio, documents: IconDocument, captures: IconCapture };

// Escape dismisses the dialog, focus moves into it on open and returns to the
// opener on close. It is role=dialog aria-modal, so a keyboard user previously
// had no way out but hunting for the X.
//
// FOCUS-STEAL FIX (capture100 2026-08-09, closing an independent-critic finding
// on PR #2352): the effect must (re)bind and move focus ONLY on an open->close
// transition, never on an unrelated parent re-render. Both call sites pass an
// inline arrow `onClose`, so a NEW `onClose` identity is created every render;
// with `onClose` in the dep array a parent re-render (e.g. a Redux selector
// firing in MediaSelection) tore the effect down and re-ran it, and the re-run
// called `modalRef.current.focus()` again — yanking focus out of the search
// input mid-typing. Reading `onClose` through a ref keeps its latest value
// without making it a dependency; the deps are now the two STABLE values
// (`open` + the parent `useRef` `modalRef`), so the focus move fires once per
// open, not per keystroke.
function useEscapeToClose(open, onClose, modalRef) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      if (onCloseRef.current) onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    if (modalRef.current) modalRef.current.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (opener && typeof opener.focus === "function") opener.focus();
    };
  }, [open, modalRef]);
}

// Empty grid — THREE distinct states, never collapsed to one (QA-1220):
//   1. filteredOut  — a type-restricted context is hiding eligible-but-wrong-type
//      media; SAY so and name the count (telling a user with 25 files "Nothing
//      here yet" is how the "Show all" toggle read as broken).
//   2. libraryEmpty — the Library genuinely holds NOTHING; the onboarding copy
//      ("Upload media, then reuse it here") is correct only here.
//   3. otherwise    — the Library HAS media but the current search/filter matched
//      none of it; a NO-RESULTS message, NOT the upload-onboarding copy. Telling a
//      user with 800+ items that they have none and must go upload was the bug.
function PickerEmptyState({ filteredOut, hiddenCount, contextLabel, libraryEmpty, query }) {
  if (filteredOut) {
    const plural = hiddenCount !== 1;
    return (
      <>
        <div className="h">No eligible media here</div>
        <div className="p">
          {hiddenCount} item{plural ? "s" : ""} in your Library can’t be used{" "}
          {contextLabel ? `as ${contextLabel}` : "here"}. Turn on “Show all” to see{" "}
          {plural ? "them" : "it"} (grayed out).
        </div>
      </>
    );
  }
  if (libraryEmpty) {
    return (
      <>
        <div className="h">Nothing here yet</div>
        <div className="p">Upload media from the Library, then reuse it here.</div>
      </>
    );
  }
  const q = (query || "").trim();
  return (
    <>
      <div className="h">No results</div>
      <div className="p">
        {q ? <>No media matches “{q}”. Try a different search.</> : "No media matches your search."}
      </div>
    </>
  );
}

// loading / error / empty / list / grid — one switch, kept out of the picker body.
function PickerResults({ status, items, onRetry, empty, mode, selectedIds, onActivate, isDisabled, disabledReason }) {
  if (status === "loading") {
    return (
      <S.Grid cols={4} data-testid="picker-loading">
        {Array.from({ length: 8 }).map((_, i) => (
          <S.Skeleton key={i} style={{ aspectRatio: "16 / 10" }} aria-hidden="true" />
        ))}
      </S.Grid>
    );
  }
  if (status === "error") {
    return (
      // Shared ErrorWheel + role="alert". This branch sits directly above an
      // EMPTY branch using the same shell, so the mark is what tells a broken
      // load apart from an empty one.
      <S.ErrorWrap data-testid="picker-error" role="alert">
        <ErrorWheel variant="section" decorative style={{ marginBottom: 16 }} />
        <div className="h">Couldn’t load your library</div>
        <div className="p">Something went wrong reaching your media.</div>
        <S.RetryButton type="button" onClick={onRetry}>Retry</S.RetryButton>
      </S.ErrorWrap>
    );
  }
  if (items.length === 0) {
    return <S.ErrorWrap data-testid="picker-empty">{empty}</S.ErrorWrap>;
  }
  if (mode === "list") {
    return (
      <MediaListTable
        items={items}
        selectedIds={selectedIds}
        onActivate={onActivate}
        showKebab={false}
        isDisabled={isDisabled}
        disabledReason={disabledReason}
      />
    );
  }
  return (
    <MediaGrid
      items={items}
      cols={4}
      compact
      selectedIds={selectedIds}
      showHoverPill={false}
      onActivate={onActivate}
      isDisabled={isDisabled}
      disabledReason={disabledReason}
    />
  );
}

export default function MediaLibraryPicker({
  open = true,
  items = [],
  acceptedTypes = null,
  contextLabel = null,
  status = "ready", // 'loading' | 'error' | 'ready'
  onRetry,
  onClose,
  onInsert,
}) {
  const [activeType, setActiveType] = useState(() => defaultActiveTab(acceptedTypes));
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("large"); // 'large' (03) | 'list' (06)
  const [selectedId, setSelectedId] = useState(null);
  const [showAllTypes, setShowAllTypes] = useState(false); // default OFF — filtered view

  // Show all ON lifts the type restriction at the TAB layer too, so the tabs
  // holding the just-un-hidden media are actually reachable (see pickerTypeTabs).
  const tabs = useMemo(
    () => pickerTypeTabs(TYPE_TABS, acceptedTypes, showAllTypes),
    [acceptedTypes, showAllTypes]
  );
  // Clamp to a visible tab (acceptedTypes could hide the current one).
  const effectiveType = tabs.some((t) => t.key === activeType) ? activeType : "all";
  const restrictsTypes = hasTypeRestriction(acceptedTypes);

  const tabFiltered = useMemo(
    () => sortMedia(filterMedia(items, { activeType: effectiveType, query }), "newest"),
    [items, effectiveType, query]
  );
  // Type-eligibility layer, composed on top of the tab/search filter above —
  // applies no matter which tab is active (all / captures / a single-type tab).
  // Default (showAllTypes false): hide anything outside acceptedTypes. Toggle
  // ON: keep everything; MediaGrid/MediaListTable render the rest disabled.
  const shown = useMemo(() => {
    if (!restrictsTypes || showAllTypes) return tabFiltered;
    return tabFiltered.filter((it) => isItemEligible(it, acceptedTypes));
  }, [tabFiltered, restrictsTypes, showAllTypes, acceptedTypes]);
  // Library-wide (search-scoped) hidden count — NOT per-tab. On the single-type
  // tab the picker auto-opens, a per-tab count is always 0, which made both the
  // "(N hidden)" label and the "turn on Show all" empty copy silently vanish.
  const searchScoped = useMemo(
    () => filterMedia(items, { activeType: "all", query }),
    [items, query]
  );
  const hiddenCount = useMemo(
    () => hiddenByTypeCount(searchScoped, acceptedTypes),
    [searchScoped, acceptedTypes]
  );

  const modalRef = useRef(null);
  useEscapeToClose(open, onClose, modalRef);

  if (!open) return null;

  // Flipping the toggle also has to move the view, or ON is a no-op whenever the
  // open tab is empty under the restriction (the common case: the auto-opened
  // single-type tab in a library with no media of that type).
  const onShowAllChange = (next) => {
    setShowAllTypes(next);
    setActiveType(
      next
        ? tabOnShowAllOn(effectiveType, tabFiltered.length)
        : tabOnShowAllOff(effectiveType, TYPE_TABS, acceptedTypes)
    );
  };

  const selectedItem = selectedId != null ? shown.find((i) => i.id === selectedId) : null;
  const selectedIds = selectedItem ? [selectedItem.id] : [];
  const eligible = (item) => isItemEligible(item, acceptedTypes);
  const toggle = (item) => {
    if (!eligible(item)) return; // insert-guard belt-and-suspenders
    setSelectedId((cur) => (cur === item.id ? null : item.id));
  };
  const insertAllowed = canInsert(selectedItem, acceptedTypes);
  const doInsert = () => {
    if (selectedItem && insertAllowed && onInsert) onInsert(selectedItem);
  };

  return (
    <S.Backdrop role="dialog" aria-modal="true" aria-label="Media Library picker" data-testid="media-picker" onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <S.Modal w={768} ref={modalRef} tabIndex={-1}>
        <S.ModalHead>
          <div>
            <div className="t">Media Library</div>
            <div className="sub">Pick anything you have already uploaded — it stays in your Library.</div>
          </div>
          <button type="button" className="x" aria-label="Close" onClick={onClose}><IconClose size={20} /></button>
        </S.ModalHead>

        <S.ModalBody>
          <S.ControlsRow style={{ borderBottom: "none", marginBottom: 16, paddingBottom: 0 }}>
            <S.Tabs role="tablist" aria-label="Media type">
              {tabs.map((t) => {
                const Ico = TAB_ICON[t.key];
                return (
                  <S.Tab key={t.key} role="tab" aria-selected={effectiveType === t.key} active={effectiveType === t.key ? 1 : 0} onClick={() => setActiveType(t.key)}>
                    <Ico size={18} /> {t.label}
                  </S.Tab>
                );
              })}
            </S.Tabs>
            <S.RightControls>
              {restrictsTypes && (
                <S.ShowAllToggle>
                  <span style={{ position: "relative", display: "inline-flex" }}>
                    <S.ShowAllInput
                      type="checkbox"
                      role="switch"
                      checked={showAllTypes}
                      aria-checked={showAllTypes}
                      onChange={(e) => onShowAllChange(e.target.checked)}
                      aria-label="Show all media types, including ones that can't be used here"
                      data-testid="picker-show-all-toggle"
                    />
                    <S.ShowAllTrack checked={showAllTypes ? 1 : 0} aria-hidden="true" />
                  </span>
                  Show all{!showAllTypes && hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ""}
                </S.ShowAllToggle>
              )}
              <S.SearchBox>
                <IconSearch size={18} />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search media" aria-label="Search media" />
              </S.SearchBox>
              <S.ViewToggle role="group" aria-label="View">
                <S.ViewSeg active={mode === "large" ? 1 : 0} aria-pressed={mode === "large"} aria-label="Grid view" onClick={() => setMode("large")}><IconGridLarge size={18} /></S.ViewSeg>
                <S.ViewSeg active={mode === "list" ? 1 : 0} aria-pressed={mode === "list"} aria-label="List view" onClick={() => setMode("list")}><IconList size={18} /></S.ViewSeg>
              </S.ViewToggle>
            </S.RightControls>
          </S.ControlsRow>

          <div style={{ maxHeight: "42vh", overflow: "auto", paddingBottom: 8 }} aria-busy={status === "loading"}>
            <PickerResults
              status={status}
              items={shown}
              onRetry={onRetry}
              mode={mode}
              selectedIds={selectedIds}
              onActivate={toggle}
              isDisabled={(item) => !eligible(item)}
              disabledReason={(item) => ineligibleReason(item, contextLabel)}
              empty={(
                <PickerEmptyState
                  filteredOut={restrictsTypes && !showAllTypes && hiddenCount > 0}
                  hiddenCount={hiddenCount}
                  contextLabel={contextLabel}
                  libraryEmpty={items.length === 0}
                  query={query}
                />
              )}
            />
          </div>
        </S.ModalBody>

        <S.ModalFoot style={{ justifyContent: "space-between" }}>
          <S.CaptionChip><IconTarget size={15} /> Reused across Wheels — nothing is uploaded twice</S.CaptionChip>
          <div style={{ display: "flex", gap: 12 }}>
            <S.GhostButton onClick={onClose}>Cancel</S.GhostButton>
            <S.PrimaryButton
              disabled={!selectedItem || !insertAllowed}
              style={!selectedItem || !insertAllowed ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              onClick={doInsert}
            >
              Add
            </S.PrimaryButton>
          </div>
        </S.ModalFoot>
      </S.Modal>
    </S.Backdrop>
  );
}
